/**
 * The route table, as data.
 *
 * Every application route lives BENEATH a locale segment (CON-002), so the
 * paths here are relative to `/:locale`.
 *
 * Five screens ship and they are the whole product:
 *
 *   ''         the SPLASH — the front door, and a brand moment rather than a
 *              marketing page. It hands over to the garage.
 *   /garage    the CATALOGUE. Every car, as artwork.
 *   /duel      the GAME. Two cars, one figure, one streak (SPEC-007).
 *   /compare   the INSTRUMENT. Two cars on every figure, plus a simulated race
 *              whose surface and gradient the reader chooses (SPEC-008).
 *   /play      the CARD GAME. A hand of five, a figure named, and a pot taken
 *              by the last card nobody can beat (SPEC-011).
 *   /jukebox   the SOUNDTRACK. Eight tracks that keep playing under whatever
 *              screen the reader moves to next (SPEC-014).
 *   /cars/:id  one car, as a card in a swipeable deck.
 *
 * `PRIMARY_NAVIGATION` is the subset the tab bar shows, in tab order. The brand
 * sheet's §12 draws five tabs — Garage, Collection, Events, Rankings, Profile —
 * and four of them still have nothing behind them. A tab that opens an empty
 * screen is worse than a tab that is not there, so they stay out of the bar
 * until they have content.
 *
 * The competition and the card game are a SIXTH and SEVENTH area, and neither
 * claims to be one of those four. The sheet's five are a composition rather than
 * a closed set, and calling the game "Events" to stay inside the drawing would
 * be a label that describes nothing a reader would find behind it (SPEC-007
 * D-21, SPEC-008).
 *
 * `/compare` — the duel — IS A ROUTE WITHOUT A TAB (SPEC-008 D-62). It is
 * reached from the two screens that give a reader something to compare: a car's
 * own page, which fills the first lane with that car, and the end of a
 * competition run, which fills both with the pair that ended it. A tab is the
 * right shape for a place you go to start something and the wrong one for a
 * screen whose whole content is two cars you have to choose first.
 */

export const ROUTE_PATHS = {
    splash: '',
    garage: '/garage',
    duel: '/duel',
    compare: '/compare',
    play: '/play',
    jukebox: '/jukebox',
    car: '/cars',
};

/** `/cars/ferrari-f40`, but not `/cars` — one car, not the whole shelf. */
const CAR_DETAIL_PATTERN = /^\/cars\/[^/]+$/u;

export function isCarDetailRoute(routePath) {
    return CAR_DETAIL_PATTERN.test(routePath);
}

/**
 * Which chrome a route wears.
 *
 * Three shapes, not a pile of booleans at the call site:
 *
 *   SPLASH    no header, no tab bar. It is the brand arriving, and any chrome
 *             on it is furniture in front of the one thing it is for.
 *   APP       header and tab bar — the garage.
 *   IMMERSIVE artwork to the top edge, its own back button, one action pinned
 *             to the foot. A car detail card, exactly as the sheet draws it.
 */
export function chromeForRoute(routePath) {
    if (isCarDetailRoute(routePath)) {
        return { header: false, tabBar: false, immersive: true, splash: false };
    }

    if (routePath === '/' || routePath === ROUTE_PATHS.splash) {
        return { header: false, tabBar: false, immersive: false, splash: true };
    }

    return { header: true, tabBar: true, immersive: false, splash: false };
}

/**
 * THE OVERTAKE IS A ROUTE WITHOUT A TAB, for now and for a different reason than
 * `/compare` is one.
 *
 * `/compare` has no tab because a tab is the wrong SHAPE for it — it is a screen
 * whose whole content is two cars you have to choose first (D-62). `/play` has
 * no tab because the card game is not finished, and a tab that opens something
 * half-built is worse than a tab that is not there — which is the same argument
 * that keeps the brand sheet's Collection, Events, Rankings and Profile out of
 * the bar. It keeps its route, so every link to it still works and the suite
 * still plays it; it simply is not offered as one of three destinations until it
 * is done.
 *
 * The jukebox takes the third seat in the meantime (SPEC-014 D-3). Restoring the
 * Overtake is this list, and nothing else.
 */
export const PRIMARY_NAVIGATION = [
    { key: 'garage', path: ROUTE_PATHS.garage, messageKey: 'nav.garage' },
    { key: 'duel', path: ROUTE_PATHS.duel, messageKey: 'nav.duel' },
    { key: 'jukebox', path: ROUTE_PATHS.jukebox, messageKey: 'nav.jukebox' },
];
